import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import {
    Modal,
    Paper,
    makeStyles,
    Theme,
    Slide,
    IconButton,
    Typography,
    Box,
    CircularProgress,
    Tabs,
    Tab,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'

import { CompanyWithInvoiceCode } from '../Admin/Property/types'
import { SimpleAreaFacilities } from './SimpleAreaFacilities'
import { NewAreaFacilities } from './NewAreaFacilities'
import { PeriodOption } from '../../models/Frequency/types'
import { InspectionType } from '../../models/Config/types'
import { FacilitiesState } from '../../hooks/useFacilitiesPlan/types'
import { useFacilitiesPlan } from '../../hooks/useFacilitiesPlan/useFacilitiesPlan'

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`facilities-tabpanel-${index}`}
            aria-labelledby={`facilities-tab-${index}`}
            {...other}
        >
            {value === index && <Box p={3}>{children}</Box>}
        </div>
    )
}

interface FacilitiesPlanModalProps {
    open: boolean
    onClose: () => void
    company: CompanyWithInvoiceCode
}

const useStyles = makeStyles((theme: Theme) => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        position: 'relative',
        backgroundColor: theme.palette.background.paper,
        boxShadow: theme.shadows[5],
        outline: 'none',
        minWidth: '600px',
        maxWidth: 'calc(100vw - 100px)',
        width: '80%',
        height: 'calc(100vh - 200px)',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: theme.shape.borderRadius,
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        zIndex: 1,
    },
    header: {
        padding: theme.spacing(1, 2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    content: {
        padding: theme.spacing(1),
        position: 'relative',
        flex: 1,
        overflow: 'hidden',
    },
    loadingOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        zIndex: 1,
    },
    tabs: {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
}))

export const FacilitiesPlanModal: React.FC<FacilitiesPlanModalProps> = ({
    open,
    onClose,
    company,
}) => {
    const classes = useStyles()
    const [tabValue, setTabValue] = useState(0)
    const {
        inspectionTypes,
        inspectionUnits,
        isLoading,
        facilities,
        facilitiesState,
        handleFacilityChange,
        handlePeriodChange,
        handleUnitChange,
        handleInspectionTypeChange,
        resetAll,
        getInspectionTypes,
        getInspectionUnits,
        createSingleAreaFacilitiesPlan,
        // New area facilities state and handlers
        facilityConfigs,
        newAreaConfigList,
        handleUpdateNewAreaFacilityConfig,
        handleAddNewAreaConfig,
        createAreaConfig,
        createNewAreaFacilitiesPlan,
    } = useFacilitiesPlan()

    useEffect(() => {
        if (open && company) {
            // Reset state when modal opens
            resetAll()
            // Fetch inspection types and units
            Promise.all([
                getInspectionTypes(company.id),
                getInspectionUnits(company.id),
            ]).catch((error) => {
                toast.error('Error fetching data:', error)
            })
        }
    }, [open, company])

    const handleFacilitiesChange = (state: {
        facilities: FacilitiesState
        inspectionTypes: InspectionType[]
    }) => {
        // Update the facilities state
        Object.entries(state.facilities).forEach(
            ([id, facility]: [
                string,
                { value: 'yes' | 'no' | null; period: PeriodOption },
            ]) => {
                handleFacilityChange(id, facility.value)
                if (facility.period) {
                    handlePeriodChange(id, facility.period)
                }
            },
        )
    }

    const handleSubmitSingleArea = () => {
        createSingleAreaFacilitiesPlan(company.id)
    }

    const handleSubmitNewArea = () => {
        createNewAreaFacilitiesPlan(company.id)
    }

    const handleTabChange = (
        event: React.ChangeEvent<{}>,
        newValue: number,
    ) => {
        setTabValue(newValue)
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            className={classes.modal}
            closeAfterTransition
            BackdropProps={{
                timeout: 500,
            }}
        >
            <Slide in={open} direction="up">
                <Paper className={classes.paper}>
                    <Box className={classes.header}>
                        <IconButton
                            className={classes.closeButton}
                            onClick={onClose}
                            size="small"
                        >
                            <CloseIcon />
                        </IconButton>

                        <Typography variant="h5" component="h2">
                            Build Facilities Plan: {company.name}
                        </Typography>
                    </Box>

                    <Box className={classes.tabs}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            aria-label="facilities plan tabs"
                        >
                            <Tab
                                label="New Area Facility Plans"
                                id="facilities-tab-0"
                                aria-controls="facilities-tabpanel-0"
                            />
                            <Tab
                                label="Single Area Facility Plans"
                                id="facilities-tab-1"
                                aria-controls="facilities-tabpanel-1"
                            />
                        </Tabs>
                    </Box>

                    {/* Content */}
                    <Box className={classes.content}>
                        {isLoading && (
                            <div className={classes.loadingOverlay}>
                                <CircularProgress />
                            </div>
                        )}

                        <TabPanel value={tabValue} index={0}>
                            <NewAreaFacilities
                                apartmentId={company.id}
                                facilityConfigs={facilityConfigs}
                                availableInspectionTypes={inspectionTypes}
                                inspectionUnits={inspectionUnits}
                                onNewAreaFacilityChange={
                                    handleUpdateNewAreaFacilityConfig
                                }
                                onAddNewAreaConfig={handleAddNewAreaConfig}
                                onSubmit={handleSubmitNewArea}
                                createAreaConfig={createAreaConfig}
                                newAreaConfigList={newAreaConfigList}
                            />
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            <SimpleAreaFacilities
                                onChange={handleFacilitiesChange}
                                availableInspectionTypes={inspectionTypes}
                                inspectionUnits={inspectionUnits}
                                facilities={facilities}
                                facilitiesState={facilitiesState}
                                onFacilityChange={handleFacilityChange}
                                onPeriodChange={handlePeriodChange}
                                onUnitChange={handleUnitChange}
                                onInspectionTypeChange={
                                    handleInspectionTypeChange
                                }
                                onSubmit={handleSubmitSingleArea}
                            />
                        </TabPanel>
                    </Box>
                </Paper>
            </Slide>
        </Modal>
    )
}
